import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { slideAction, saveMirrorToCloudinary } from '../../../../store/slideSlice';
import { slideAPI } from '../../../../store/api';
import { emitShapeTextUpdate } from '../../../../Socket/Socket';
import { getSnapshot } from '../../../../utils/utils';
import { updateTextboxDelta } from '../../../../utils/helper';
import LayoutEnum from '../../../../utils/layoutEnum';

/**
 * Shape text persistence: debounced Redux update, mirror snapshot, database
 * write, socket broadcast, and master-slide fan-out.
 *
 * Lifted verbatim out of Shape.js during the geometry rebuild — same dispatches,
 * same 500ms debounce, same ordering. Nothing here was rewritten, because none
 * of it relates to the geometry defects the rebuild addresses.
 */
export const useShapePersistence = ({
  shapeId,
  currentSlideId,
  quillRef,
  addResizer,
  removeResizer,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [shapeText, setShapeText] = useState();

  const { updateShapeTextInDatabase } = slideAPI;
  const isMasterSlide = useSelector((state) => state.slide.isMasterSlide);
  const masterSlideId = useSelector((state) => state.slide.masterSlideId);
  const existingSlides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);
  const shapeData = useSelector(
    (state) => state.slide.slides[currentSlideId]?.shapes[shapeId]
  );

  const updateDelta = (deltaState, slideId, masterMode, masterId, slides) => {
    if (deltaState && slideId && !masterMode) {
      removeResizer(document.getElementById(shapeId));
      getSnapshot().then((dataUrl) => {
        dispatch(slideAction.updateShapeText({ slideId, ...deltaState }));
        dispatch(saveMirrorToCloudinary({
          dataId: slideId,
          mirrorSlideDataUrl: dataUrl,
          presentationId: id,
        }));
        const payload = {
          shapeId,
          content: quillRef.current.getContents(),
          currentSlideId: slideId,
        };
        const updatedSlides = updateTextboxDelta(slides, payload, 'shape');
        const data = { slides: updatedSlides, mirrorSlides };
        updateShapeTextInDatabase(
          id, slideId, shapeId, quillRef.current.getContents(), dispatch, data
        );
        emitShapeTextUpdate({
          roomId: id,
          elementData: {
            slideId,
            content: quillRef.current.getContents(),
            shapeId,
          },
          mirrorData: { dataId: slideId, mirrorSlideDataUrl: dataUrl },
        });
        addResizer(document.getElementById(shapeId));
      });
    } else if (deltaState && masterMode && slideId.includes('master')) {
      dispatch(slideAction.updateShapeText({ slideId: masterId, ...deltaState }));
      const isMainMasterSlide = !Object.values(LayoutEnum).some((layout) =>
        slideId.toLowerCase().includes(layout.toLowerCase())
      );
      if (isMainMasterSlide) {
        Object.keys(slides).forEach((key) => {
          const isMasterLayout = Object.values(LayoutEnum).some((enumValue) =>
            key.toLowerCase().includes(enumValue.toLowerCase())
          );
          if (isMasterLayout) {
            dispatch(slideAction.updateShapeText({ slideId: key, ...deltaState }));
          }
        });
      }
      const layoutType = isMainMasterSlide ? null : slides[masterId].layoutType;
      slideAPI.updateMultipleElementTextInDatabase(
        id, shapeId, quillRef.current.getContents(), layoutType
      );
    }
  };

  // Held in a ref so the debounce timer survives re-renders, exactly as before.
  const updateTextDebounce = useRef(
    debounce((deltaState, slideId, masterMode, masterId, slides) => {
      updateDelta(deltaState, slideId, masterMode, masterId, slides);
    }, 500)
  ).current;

  useEffect(() => {
    if (shapeText && shapeData?.delta) {
      const currentContent = quillRef.current.getContents();
      if (currentContent?.ops?.[0]?.insert !== shapeData.delta?.ops?.[0]?.insert) {
        if (!currentSlideId.includes('master')) {
          dispatch(slideAction.updateIsSaving({ isSaving: 'Saving...', lastSavedDateTime: '' }));
        }
        updateTextDebounce(shapeText, currentSlideId, isMasterSlide, masterSlideId, existingSlides);
      }
    }
    return () => {
      updateTextDebounce.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeText]);

  return { setShapeText };
};

export default useShapePersistence;
